//* 편리정보 > 자주쓰는 상주말씀 관리 — 내 즐겨찾기 등록·수정·삭제 + 프리셋에서 가져오기
//* 저장 위치: user.{role}Detail.mournerTextList (스펙: docs/bugo-mourner-text-favorites-spec.md)
import React, { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  MournerTextPreset,
  useMournerTextPresets,
} from '@shared/api/fevent/mournerTextPreset';
import { Button } from '@shared/components/Button';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';

import {
  MOURNER_TEXT_MAX_COUNT,
  MOURNER_TEXT_MAX_LENGTH,
  normalizeMournerTextList,
  readMyDetailList,
  saveMyDetailList,
} from '../../utils/myDetailList.util';

const OLD_SERVER_MESSAGE =
  '서버가 아직 상주말씀 즐겨찾기를 지원하지 않습니다. 서버 업데이트 후 다시 시도해 주세요.';

const PreferMournerTextEditor: FC = () => {
  const { userProfile, bugoCamelRole, refreshUserProfile } = useAuth();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);
  const camelRole = bugoCamelRole();

  //* 서버에 저장된 목록(정본)
  const savedList = useMemo(
    () => readMyDetailList(userProfile, camelRole, 'mournerTextList'),
    [userProfile, camelRole],
  );

  //* 화면 편집본: 저장된 행의 편집값 + 아직 저장 안 한 새 행. 표시 순서는 [저장분..., 새 행...]
  const [savedEdits, setSavedEdits] = useState<string[]>(savedList);
  const [newRows, setNewRows] = useState<string[]>([]);
  //* 프로필이 갱신되면(저장 후 refresh·마운트 갱신) 저장분 편집값을 정본에 다시 맞춘다
  useEffect(() => {
    setSavedEdits(savedList);
  }, [savedList]);
  const localList = useMemo(() => [...savedEdits, ...newRows], [savedEdits, newRows]);

  const [saving, setSaving] = useState(false);
  const [showMorePresets, setShowMorePresets] = useState(false);
  const { data: presets } = useMournerTextPresets();

  //* onSaved: 서버 저장 직후·프로필 갱신 전에 호출(새 행 정리 등)
  const persist = async (
    next: string[],
    successMessage: string,
    onSaved?: () => void,
  ) => {
    if (!userProfile) return false;
    const normalized = normalizeMournerTextList(next);
    setSaving(true);
    try {
      const { persisted } = await saveMyDetailList(
        userProfile,
        camelRole,
        'mournerTextList',
        normalized,
      );
      if (!persisted) {
        notificateError(notificationInstance, OLD_SERVER_MESSAGE);
        return false;
      }
      notificateSuccess(notificationInstance, successMessage);
      onSaved?.();
      await refreshUserProfile();
      return true;
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '상주말씀 저장에 실패했습니다');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
    const value = event.target.value;
    if (index < savedEdits.length) {
      setSavedEdits((curr) => curr.map((v, i) => (i === index ? value : v)));
    } else {
      const newIndex = index - savedEdits.length;
      setNewRows((curr) => curr.map((v, i) => (i === newIndex ? value : v)));
    }
  };

  //* 수정(저장된 행) 또는 완료(새 행)
  const onCommit = async (index: number) => {
    const text = (localList[index] ?? '').trim();
    if (!text) return;
    const isNew = index >= savedList.length;
    if (!isNew && savedList[index] === text) return; // 변경 없음
    const next = [...savedList];
    if (isNew) next.push(text);
    else next[index] = text;
    const newIndex = index - savedList.length;
    await persist(
      next,
      isNew ? '상주말씀을 등록했습니다' : '상주말씀을 수정했습니다',
      isNew
        ? () => setNewRows((curr) => curr.filter((_, i) => i !== newIndex))
        : undefined,
    );
  };

  //* 삭제(저장된 행) 또는 취소(새 행)
  const onRemove = async (index: number) => {
    if (index >= savedList.length) {
      const newIndex = index - savedList.length;
      setNewRows((curr) => curr.filter((_, i) => i !== newIndex));
      return;
    }
    const next = savedList.filter((_, i) => i !== index);
    await persist(next, '상주말씀을 삭제했습니다');
  };

  const onAdd = () => setNewRows((curr) => [...curr, '']);

  //* 프리셋 → 내 목록에 바로 저장
  const onImportPreset = async (preset: MournerTextPreset) => {
    if (savedList.includes(preset.text)) return;
    await persist([...savedList, preset.text], '프리셋을 내 목록에 추가했습니다');
  };

  const savedSet = useMemo(() => new Set(savedList), [savedList]);
  const featured = presets?.featured ?? [];
  const more = presets?.more ?? [];
  const hasPresets = featured.length + more.length > 0;
  const reachedMax = savedList.length >= MOURNER_TEXT_MAX_COUNT;

  const presetRow = (preset: MournerTextPreset) => {
    const added = savedSet.has(preset.text);
    return (
      <div
        key={preset.key}
        className="flex items-start justify-between gap-2 border-b py-2 theme-border-1 last:border-b-0"
      >
        <p className="min-w-0 flex-1 break-keep text-xs leading-5 theme-text-1">
          {preset.text}
        </p>
        <Button
          type="button"
          disabled={added || saving || reachedMax}
          onClick={() => onImportPreset(preset)}
          className={`shrink-0 rounded-none border px-2 py-1 text-xs font-medium shadow-none theme-border-1 ${
            added ? 'theme-text-6' : ''
          }`}
        >
          {added ? '추가됨' : '추가'}
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* 내 즐겨찾기 */}
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <h5 className="font-medium">내 상주말씀</h5>
          <p className="text-xs theme-text-8">
            {savedList.length}/{MOURNER_TEXT_MAX_COUNT}개 · 1건 {MOURNER_TEXT_MAX_LENGTH}
            자
          </p>
        </div>
        {localList.length === 0 && (
          <p className="py-2 text-xs theme-text-8">
            등록된 상주말씀이 없습니다. 아래에서 추가하거나 프리셋에서 가져오세요.
          </p>
        )}
        <div className="space-y-1">
          {localList.map((text, index) => {
            const isNew = index >= savedList.length;
            return (
              <div
                key={`mourner-text-${index}`}
                className="flex items-stretch justify-between gap-1"
              >
                <div className="flex-1">
                  <textarea
                    value={text}
                    maxLength={MOURNER_TEXT_MAX_LENGTH}
                    rows={2}
                    placeholder="상주 말씀"
                    onChange={(event) => onTextChange(event, index)}
                    className="input-box w-full border p-2 text-sm font-medium theme-text-1 theme-bg-1 theme-border-1 placeholder:theme-text-6"
                  />
                </div>
                <div className="flex flex-col items-stretch gap-1">
                  <Button
                    type="button"
                    disabled={saving}
                    onClick={() => onCommit(index)}
                    className="flex-1 rounded-none border px-2 font-medium shadow-none theme-border-1"
                  >
                    {isNew ? '완료' : '수정'}
                  </Button>
                  <Button
                    type="button"
                    disabled={saving}
                    onClick={() => onRemove(index)}
                    className="button flex-1 rounded-none border px-2 font-medium shadow-none theme-border-1"
                  >
                    {isNew ? '취소' : '삭제'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="center-box">
          <Button
            type="button"
            disabled={saving || reachedMax}
            onClick={onAdd}
            className="button-fold border px-8 py-1.5 text-sm font-medium theme-border-1"
          >
            추가
          </Button>
        </div>
      </div>

      {/* 프리셋에서 가져오기 */}
      {hasPresets && (
        <div className="space-y-1">
          <h5 className="font-medium">자주 쓰는 문구에서 가져오기</h5>
          <p className="text-xs theme-text-8">
            실제로 많이 쓰인 문구입니다. [추가]를 누르면 내 상주말씀에 저장됩니다.
          </p>
          <div>
            {featured.map(presetRow)}
            {showMorePresets && more.map(presetRow)}
          </div>
          {more.length > 0 && (
            <button
              type="button"
              onClick={() => setShowMorePresets((v) => !v)}
              className="w-full py-1.5 text-center text-xs font-medium theme-text-8"
            >
              {showMorePresets ? '접기 ▴' : `더보기 (${more.length}) ▾`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PreferMournerTextEditor;
